import { useState } from "react";
import {
  Autocomplete,
  IconButton,
  Popover,
  Switch,
  TextField,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import RefreshIcon from "@mui/icons-material/Refresh";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import type { FormRow } from "../types";
import { buildDefaultValue, buildRequiredDefaults } from "../seedRequiredDefaults";
import { useUseEndpointActions, useUseEndpointValidation } from "../../../contexts";

export interface ActionsCellContext {
  draft: any;
  addArrayItem: (path: (string | number)[], item: unknown) => void;
  removeArrayItem: (path: (string | number)[], index: number) => void;
  addOptionalField: (parentPath: (string | number)[], key: string, defaultValue: unknown) => void;
  removeOptionalField: (parentPath: (string | number)[], key: string) => void;
  toggleOptionalSection: (path: (string | number)[], enabled: boolean, defaultBranchData: unknown) => void;
  commit: (path: (string | number)[], value: unknown, label: string) => void;
}

export const ActionsCellRenderer = (props: { data?: FormRow; context?: ActionsCellContext }) => {
  const row = props.data;
  const ctx = props.context;
  const { callUseEndpoint } = useUseEndpointActions();
  const { missingPathsSet } = useUseEndpointValidation();
  const [fetching, setFetching] = useState(false);
  const [optAnchor, setOptAnchor] = useState<HTMLElement | null>(null);

  if (!row || !ctx) return null;

  const pathDot = row.path.map(String).join(".");
  const isMissing = missingPathsSet?.has(pathDot) ?? false;

  const handleFetch = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (!row.useEndpoint || !callUseEndpoint) return;
    setFetching(true);
    try {
      const result = await callUseEndpoint(row.useEndpoint, ctx.draft);
      if (result.success && result.data && typeof result.data === "object") {
        const fetched = (result.data as any)[row.fieldName];
        if (fetched !== undefined) {
          ctx.commit(row.path, fetched, row.label);
        }
      }
    } finally {
      setFetching(false);
    }
  };

  const buttons: React.ReactNode[] = [];

  if (row.useEndpoint) {
    buttons.push(
      <Tooltip
        key="fetch"
        title={isMissing ? "Required for fetch request" : "Fetch value"}
        arrow
      >
        <span>
          <IconButton
            size="small"
            disabled={fetching}
            onClick={handleFetch}
            sx={{ color: isMissing ? "#ff1744" : "#fff" }}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    );
  }

  if (row.isOptionalSectionParent) {
    buttons.push(
      <Tooltip key="enable" title={row.optionalSectionEnabled ? "Disable section" : "Enable section"} arrow>
        <Switch
          size="small"
          checked={row.optionalSectionEnabled}
          onClick={(e) => e.stopPropagation()}
          onChange={(_, checked) => {
            const firstBranch = row.oneOfBranches?.[0]?.schema;
            const defaults = checked && firstBranch ? buildDefaultValue(firstBranch) : undefined;
            ctx.toggleOptionalSection(row.path, checked, defaults);
          }}
        />
      </Tooltip>
    );
  }

  if (row.fieldType === "array") {
    buttons.push(
      <Tooltip key="add" title="Add item" arrow>
        <IconButton
          size="small"
          sx={{ color: "#fff" }}
          onClick={(e) => {
            e.stopPropagation();
            const item = row.isAdditionalPropsParent
              ? { __key: "", ...buildRequiredDefaults(row.itemsSchema) }
              : buildDefaultValue(row.itemsSchema);
            ctx.addArrayItem(row.path, item);
          }}
        >
          <AddIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    );
  }

  const isArrayItem =
    row.parentId !== null && typeof row.path[row.path.length - 1] === "number";

  const isRemovableOptional =
    !row.required &&
    !isArrayItem &&
    !row.isOptionalSectionParent &&
    row.parentId !== null;
  if (isRemovableOptional) {
    buttons.push(
      <Tooltip key="rm-opt" title="Remove field" arrow>
        <IconButton
          size="small"
          sx={{ color: "#fff" }}
          onClick={(e) => {
            e.stopPropagation();
            const parentPath = row.path.slice(0, -1);
            const key = String(row.path[row.path.length - 1]);
            ctx.removeOptionalField(parentPath, key);
          }}
        >
          <RemoveIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    );
  }

  if (isArrayItem) {
    buttons.push(
      <Tooltip key="remove" title="Remove item" arrow>
        <IconButton
          size="small"
          sx={{ color: "#fff" }}
          onClick={(e) => {
            e.stopPropagation();
            const parentPath = row.path.slice(0, -1);
            const idx = row.path[row.path.length - 1] as number;
            ctx.removeArrayItem(parentPath, idx);
          }}
        >
          <RemoveIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    );
  }

  const hasOptional = (row.optionalChildren?.length ?? 0) > 0;
  if (hasOptional) {
    buttons.push(
      <Tooltip key="opt" title="Add optional field" arrow>
        <IconButton
          size="small"
          sx={{ color: "#fff" }}
          onClick={(e) => {
            e.stopPropagation();
            setOptAnchor(e.currentTarget);
          }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <span style={{ display: "inline-flex", gap: 2 }}>
      {buttons}
      {optAnchor && row.optionalChildren && (
        <Popover
          open={true}
          anchorEl={optAnchor}
          onClose={() => setOptAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          slotProps={{ paper: { sx: { p: 2, minWidth: 280 } } }}
        >
          <Autocomplete
            size="small"
            sx={{
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "#000" },
              "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#000" },
              "& .MuiInputLabel-root": { color: "#000" },
              "& .MuiInputLabel-root.Mui-focused": { color: "#000" },
            }}
            options={row.optionalChildren}
            getOptionLabel={(o) => (typeof o === "string" ? o : o.label)}
            renderInput={(p) => <TextField {...p} autoFocus label="Add optional field" />}
            onChange={(_, value) => {
              if (!value) return;
              ctx.addOptionalField(row.path, value.key, value.defaultValue);
              setOptAnchor(null);
            }}
          />
        </Popover>
      )}
    </span>
  );
};
